import { useState, useEffect, useCallback } from 'react';

interface Props {
  isActive: boolean;
  onSelect: (element: HTMLElement) => void;
  onCancel: () => void;
}

export const ElementSelector = ({ isActive, onSelect, onCancel }: Props) => {
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);

  const cleanup = useCallback(() => {
    if (hoveredElement) {
      hoveredElement.style.outline = '';
      hoveredElement.style.outlineOffset = '';
      setHoveredElement(null);
    }
    document.body.style.cursor = '';
  }, [hoveredElement]);

  const isEditorElement = useCallback((element: HTMLElement): boolean => {
    return !!(
      element.closest('.visual-editor-ui') ||
      element.closest('[role="dialog"]') ||
      element.tagName === 'HTML' ||
      element.tagName === 'BODY' ||
      element === document.body ||
      element === document.documentElement ||
      element.classList.contains('fixed') ||
      element.getAttribute('aria-hidden') === 'true'
    );
  }, []);

  useEffect(() => {
    if (!isActive) {
      cleanup();
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      
      if (isEditorElement(target) || target === hoveredElement) {
        return;
      }

      cleanup();

      target.style.outline = '2px solid #3b82f6';
      target.style.outlineOffset = '2px';
      setHoveredElement(target);
    };

    const handleClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const target = e.target as HTMLElement;
      
      // Check if the target is part of the editor UI
      if (target.closest('.visual-editor-ui')) {
        return;
      }
      
      if (isEditorElement(target)) {
        return;
      }

      onSelect(target);
      cleanup();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
        cleanup();
      }
    };

    document.body.style.cursor = 'crosshair';
    window.addEventListener('mousemove', handleMouseMove, { capture: true });
    window.addEventListener('click', handleClick, { capture: true });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      cleanup();
      window.removeEventListener('mousemove', handleMouseMove, { capture: true });
      window.removeEventListener('click', handleClick, { capture: true });
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, cleanup, isEditorElement, onSelect, onCancel]);

  return null;
};